import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { importApi } from "../../lib/api";
import { TID } from "../../constants/testIds";
import { getApiError } from "../../lib/utils";
import { Stat } from "./Stat";

/** Quarterly refresh tab — the safe, everyday upsert path. Descriptive
 *  fields are upserted, statuses & audit history are untouched. */
export const QuarterlyRefreshTab = ({ onImported }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const submit = async () => {
    if (!file) return;
    setLoading(true);
    setReport(null);
    try {
      const r = await importApi.upload(file);
      setReport(r);
      toast.success(`Imported: ${r.added} added, ${r.updated} updated`);
      setFile(null);
      onImported?.();
    } catch (e) {
      toast.error(getApiError(e, "Import failed"));
    } finally { setLoading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  return (
    <>
      <div
        data-testid={TID.importDropzone}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-12 text-center transition-colors ${
          dragging ? "border-nmi-600 bg-nmi-50" : "border-slate-300 bg-slate-50 hover:bg-slate-100"
        }`}
      >
        <input
          data-testid={TID.importFileInput}
          ref={inputRef}
          type="file"
          accept=".xlsx,.xlsm"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
        <div className="flex h-12 w-12 items-center justify-center rounded-md bg-white shadow-sm border border-slate-200">
          <FileSpreadsheet size={22} className="text-nmi-700" strokeWidth={2} />
        </div>
        <div className="mt-3 font-heading text-lg font-bold text-slate-900">
          {file ? file.name : "Drop your .xlsx here"}
        </div>
        <div className="mt-1 text-xs text-slate-500">
          or click to browse · only .xlsx / .xlsm accepted
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        {file && (
          <button onClick={() => setFile(null)} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">
            Clear
          </button>
        )}
        <button
          data-testid={TID.importSubmit}
          onClick={submit}
          disabled={!file || loading}
          className="inline-flex items-center gap-2 rounded-md bg-nmi-700 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-nmi-800 disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} strokeWidth={2.5} />}
          Run import
        </button>
      </div>

      {report && (
        <div className="mt-8">
          <div className="mb-3 flex items-center gap-2">
            <CheckCircle2 size={18} className="text-emerald-600" strokeWidth={2.5} />
            <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900">Import report</h2>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Stat label="Added" value={report.added} tone="good" />
            <Stat label="Updated" value={report.updated} tone="info" />
            <Stat label="Missing (flagged)" value={report.flagged_missing} tone="warn" />
            <Stat label="Rows Seen" value={report.total_seen} />
          </div>
          {report.errors?.length > 0 && (
            <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-bold text-amber-900">
                <AlertTriangle size={14} /> {report.errors.length} row errors
              </div>
              <ul className="list-disc pl-5 space-y-1 text-xs text-amber-900">
                {report.errors.map((e, i) => <li key={`err-${i}-${e.slice(0, 20)}`}>{e}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </>
  );
};
